// Chase-card entity silo: the most expensive Palworld TCG printings, split into
// small, tightly bounded tables per set instead of one endless scrolling grid.
// Answer engines extract short bounded tables; they skip long script-heavy ones.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getPriceBoard } from "@/lib/prices.functions";
import { formatMoney, type PriceRow } from "@/lib/prices-shared";
import { OFFICIAL_CARD_BY_CODE, SET_NAMES } from "@/lib/tcg-cards";
import { CHASE_VALUE_CARDS } from "@/lib/chase-value-cards";
import { RarityBadge } from "@/components/RarityBadge";
import { cardLinkProps } from "@/lib/card-link";
import { FaqSection, faqSchema, type FaqItem } from "@/components/PageFaq";
import { breadcrumbSchema, collectionSchema, datasetSchema, snippetAnswerSchema } from "@/lib/schema";
import { SnippetFunnel } from "@/components/SnippetFunnel";
import { PriceFreshness } from "@/components/PriceFreshness";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { CrossCategoryLinks } from "@/components/CrossCategoryLinks";
import { ShareButton } from "@/components/ShareButton";
import { pageTitle } from "@/lib/seo-title";

const URL = "https://palstars.com/most-expensive-palworld-cards";

const SNIPPET_Q = "What is the most expensive Palworld TCG card?";
const SNIPPET_A =
  "The most expensive Palworld OFFICIAL CARD GAME cards are the parallel chase printings — Ultra Secret Gold (OSR), Special Signature Parallel (SSP) and the Trial Deck secret parallels — rather than the base RR versions of the same card. A chase printing's value comes from its pull rate inside Dawn of Palpagos and Legends Awaken boxes, so the ranking changes whenever a set is reprinted or a tournament result moves the meta. Pal Stars ranks every printing by validated marketplace asking price and rebuilds the ranking on each daily sync.";

const FAQS: FaqItem[] = [
  {
    q: "What is the most expensive Palworld card right now?",
    a: "The top row of the ranking table on this page is the current most expensive tracked Palworld printing. It is almost always an OSR (Ultra Secret Gold) or SSP parallel from Dawn of Palpagos, because those are the rarest pulls in the set. The order is rebuilt every daily sync, so a tournament spike or a reprint can change the leader within a day.",
  },
  {
    q: "How much is an SSP alternate art Palworld card worth?",
    a: "SSP alternate art printings trade far above their base RR version of the same card number. The exact figure per printing is listed in the rarity tables on the Palworld TCG alternate art price guide, next to the TCGplayer completed-sale average where sales have been recorded.",
  },
  {
    q: "Are these prices sold prices or asking prices?",
    a: "The market column is the depth-weighted median of validated English asking listings. The Last sold column is the average of recent completed TCGplayer sales, which is money that actually changed hands. eBay does not grant Pal Stars completed-sale access, so no eBay sold figure is shown or estimated.",
  },
  {
    q: "How often are the most expensive Palworld card prices updated?",
    a: "Twice a day, every day, for every tracked printing. There is no weekly refresh rotation, so a chase card that spikes today is repriced today rather than up to a week later.",
  },
  {
    q: "Why is a base card cheaper than its parallel?",
    a: "The base printing and the parallel share the same name, number and rules text, so they are interchangeable in a 50-card deck. Only the parallel is scarce, so collectors pay the premium for the finish and the print run, not for playability.",
  },
];

export const Route = createFileRoute("/most-expensive-palworld-cards")({
  // SSR the ranking so crawlers and AI answer engines read a complete table in
  // the initial HTML with no client data hooks to wait on.
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: ["price-board"],
      queryFn: () => getPriceBoard(),
      staleTime: 5 * 60 * 1000,
    }),
  head: () => ({
    meta: [
      { title: pageTitle("Most Expensive Palworld TCG Cards") },
      {
        name: "description",
        content:
          "The most expensive Palworld TCG cards ranked by live market value: OSR, SSP and secret parallels from Dawn of Palpagos and Legends Awaken, with TCGplayer sold averages, updated daily.",
      },
      { property: "og:title", content: "Most Expensive Palworld TCG Cards — Chase Card Price Ranking" },
      {
        property: "og:description",
        content: "Live ranking of the priciest Palworld OFFICIAL CARD GAME printings, rebuilt on every daily price sync.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      breadcrumbSchema([
        ["Home", "/"],
        ["TCG Price Chart", "/prices"],
        ["Most Expensive Cards", "/most-expensive-palworld-cards"],
      ]),
      datasetSchema({
        name: "Most expensive Palworld TCG card printings",
        description:
          "Ranking of Palworld OFFICIAL CARD GAME printings by validated marketplace asking price, with TCGplayer completed-sale averages and 7-day moves.",
        path: "/most-expensive-palworld-cards",
        keywords: [
          "most expensive Palworld cards",
          "Palworld TCG chase cards",
          "Dawn of Palpagos card prices",
          "Palworld OSR card value",
          "Palworld SSP card price",
        ],
        variables: ["Market price (USD)", "TCGplayer sold average (USD)", "7-day change (%)", "Rarity", "Set"],
      }),
      snippetAnswerSchema({ question: SNIPPET_Q, answer: SNIPPET_A, path: "/most-expensive-palworld-cards" }),
      collectionSchema({
        name: "Most Expensive Palworld TCG Cards",
        description: "Directory of the highest-value Palworld OFFICIAL CARD GAME chase printings and their dedicated valuation pages.",
        path: "/most-expensive-palworld-cards",
        items: CHASE_VALUE_CARDS.map((c) => {
          const card = OFFICIAL_CARD_BY_CODE.get(c.code.toUpperCase());
          return { name: `${card?.name ?? c.code} ${c.code} price ranking`, path: `/palworld-card-value/${c.code.toUpperCase()}` };
        }),
      }),
      faqSchema(FAQS, URL),
    ],
  }),
  component: MostExpensivePage,
});

/** Sets shown as their own bounded table, in the order collectors search them. */
const SET_ORDER = ["BP01", "BP02", "TD01", "TD02", "PR"] as const;
const TOP_PER_SET = 12;

function isParallel(code: string): boolean {
  return !!OFFICIAL_CARD_BY_CODE.get(code.toUpperCase())?.variant;
}

function MostExpensivePage() {
  const { data } = useQuery({
    queryKey: ["price-board"],
    queryFn: () => getPriceBoard(),
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  });

  const rows = data?.rows ?? [];

  const overall = useMemo(
    () => [...rows].sort((a, b) => b.market - a.market).slice(0, 15),
    [rows],
  );

  const bySet = useMemo(() => {
    return SET_ORDER.map((set) => ({
      set,
      label: SET_NAMES[set] ?? set,
      rows: rows
        .filter((r) => r.set === set)
        .sort((a, b) => b.market - a.market)
        .slice(0, TOP_PER_SET),
    })).filter((g) => g.rows.length > 0);
  }, [rows]);

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
        <Link to="/" className="hover:text-primary">Home</Link> ·{" "}
        <Link to="/prices" className="hover:text-primary">TCG Price Chart</Link> ·{" "}
        <span>Most Expensive Cards</span>
      </nav>

      <header>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <h1 className="text-3xl font-extrabold sm:text-4xl">Most Expensive Palworld TCG Cards</h1>
          <ShareButton
            title="Most Expensive Palworld TCG Cards — Pal Stars"
            text="Live ranking of the priciest Palworld OFFICIAL CARD GAME chase printings, rebuilt daily."
            path="/most-expensive-palworld-cards"
          />
        </div>
        <p className="aeo-answer mt-3 max-w-3xl text-sm text-muted-foreground">
          Every tracked Palworld OFFICIAL CARD GAME printing ranked by live market value, then split by set so each
          table stays short enough to read at a glance. Chase parallels (OSR, SSP and the Trial Deck secret parallels)
          carry the highest values; base printings of the same card are far cheaper because they share the same rules
          text without the scarce finish.
        </p>
      </header>

      <PriceFreshness updated={data?.updated ?? null} />

      <SnippetFunnel
        className="mt-6"
        question={SNIPPET_Q}
        answer={SNIPPET_A}
        withheld={[
          "Today's number-one most expensive Palworld printing",
          "Live market value for every chase card",
          "TCGplayer completed-sale average per printing",
          "7-day move so you can see what is climbing",
        ]}
        cta={{ to: "/prices", label: "Open the full Palworld TCG price chart" }}
      />

      <PriceTable
        id="top-overall"
        caption="Top 15 most expensive Palworld TCG printings across all sets, by market value"
        heading="Top 15 most expensive Palworld printings"
        rows={overall}
      />

      {bySet.map((g) => (
        <PriceTable
          key={g.set}
          id={`set-${g.set.toLowerCase()}`}
          heading={`Most expensive ${g.label} cards`}
          caption={`Most expensive Palworld ${g.label} printings by market value, with TCGplayer sold averages`}
          rows={g.rows}
        />
      ))}

      <p className="mt-6 text-sm text-muted-foreground">
        Parallel-only ranking:{" "}
        <Link to="/palworld-alternate-art-price-guide" className="text-primary hover:underline">
          Palworld TCG alternate art price guide
        </Link>{" "}
        · Completed sales:{" "}
        <Link to="/tcg-sold-prices" className="text-primary hover:underline">
          Palworld TCG sold prices
        </Link>{" "}
        · Whole-market benchmark:{" "}
        <Link to="/tcg-price-index" className="text-primary hover:underline">
          Palworld TCG Price Index
        </Link>
        .
      </p>

      <div className="mt-6">
        <AffiliateDisclosure />
      </div>

      <FaqSection items={FAQS} />
      <CrossCategoryLinks side="tcg" />
    </main>
  );
}

export function PriceTable({
  id,
  heading,
  caption,
  rows,
}: {
  id: string;
  heading: string;
  caption: string;
  rows: PriceRow[];
}) {
  return (
    <section className="mt-8" aria-labelledby={`${id}-heading`}>
      <h2 id={`${id}-heading`} className="text-xl font-bold">
        {heading}
      </h2>
      <div className="glass mt-3 overflow-x-auto rounded-2xl">
        <table className="w-full min-w-[560px] text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead className="text-xs uppercase text-muted-foreground">
            <tr className="border-b border-border/60">
              <th scope="col" className="px-3 py-2 text-left">#</th>
              <th scope="col" className="px-3 py-2 text-left">Card</th>
              <th scope="col" className="px-3 py-2 text-left">Rarity</th>
              <th scope="col" className="px-3 py-2 text-right">Market</th>
              <th scope="col" className="hidden px-3 py-2 text-right sm:table-cell">TCGplayer last sold</th>
              <th scope="col" className="px-3 py-2 text-right">7d</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.code} className="border-b border-border/40 last:border-0">
                <td className="px-3 py-2 tabular-nums text-muted-foreground">{i + 1}</td>
                <td className="px-3 py-2">
                  <Link {...cardLinkProps(r.code)} className="font-semibold hover:text-primary">
                    {r.name}
                  </Link>
                  <span className="ml-2 text-xs text-muted-foreground">{r.code}</span>
                  {isParallel(r.code) ? (
                    <span className="ml-2 text-xs text-primary">parallel</span>
                  ) : (
                    <span className="ml-2 text-xs text-muted-foreground">base</span>
                  )}
                </td>
                <td className="px-3 py-2"><RarityBadge code={r.code} /></td>
                <td className="px-3 py-2 text-right font-bold tabular-nums">{formatMoney(r.market, "USD")}</td>
                <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">
                  {r.tcgSoldAvg ? formatMoney(r.tcgSoldAvg, "USD") : "—"}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  <span className={r.change7d >= 0 ? "text-emerald-400" : "text-rose-400"}>
                    {r.change7d > 0 ? "+" : ""}
                    {r.change7d.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
